import { apiClient, handleApiError } from './client';
import type {
  ServiceRequest,
  ServiceRequestVendor,
  ServiceRequestStatus,
  ServiceRequestVendorStatus,
  PostingStatus,
  CreateServiceRequestInput,
  UpdateServiceRequestInput,
  AddVendorToRequestInput,
  SubmitProposalInput,
} from '@om/shared-types';

export interface PostAcceptCondition {
  id: number;
  service_request_id: number;
  quote_id: number;
  condition_type: 'document' | 'tasting' | 'approval' | 'insurance' | 'deposit' | 'other';
  description: string;
  status: 'pending' | 'satisfied' | 'waived';
  resolved_at: string | null;
  resolved_by: number | null;
  created_at: string;
  updated_at: string;
}

export interface FinalTermsSummary {
  total: number;
  pending: number;
  satisfied: number;
  waived: number;
  all_closed: boolean;
}

export interface FinalTerms {
  service_request_id: number;
  accepted_quote: {
    id: number;
    request_id: number;
    vendor_id: number;
    quote_number: string;
    total_amount: number;
    currency: string;
    status: string;
    accepted_at: string | null;
  };
  conditions: PostAcceptCondition[];
  conditions_summary: FinalTermsSummary;
  ready_for_award: boolean;
  ready_for_signing: boolean;
}

export interface AwardVendorResult {
  request: ServiceRequest;
  booking: {
    id: number;
  };
}

export interface SignServiceRequestResult {
  request: ServiceRequest;
  booking: {
    id: number;
  };
}

type ApiEnvelope<T> = { success?: boolean; data?: T; error?: string; message?: string };

function unwrap<T>(payload: unknown, fallback: string): T {
  if (payload && typeof payload === 'object' && 'success' in (payload as Record<string, unknown>)) {
    const env = payload as ApiEnvelope<T>;
    if (env.success) return env.data as T;
    const msg = (typeof env.error === 'string' && env.error) || (typeof env.message === 'string' && env.message) || fallback;
    throw new Error(msg);
  }
  return payload as T;
}

const base = (cid: number) => `/celebrations/${cid}/service-requests`;
const vendorBase = (cid: number, rid: number) => `${base(cid)}/${rid}/vendors`;

class ServiceRequestApiService {
  async listRequests(celebrationId: number, serviceId?: number): Promise<ServiceRequest[]> {
    try {
      const params = serviceId ? `?service_id=${serviceId}` : '';
      const res = await apiClient.get(`${base(celebrationId)}${params}`);
      return unwrap<ServiceRequest[]>(res.data, 'Failed to load service requests');
    } catch (err) {
      throw new Error(handleApiError(err));
    }
  }

  async getRequest(celebrationId: number, requestId: number): Promise<ServiceRequest> {
    try {
      const res = await apiClient.get(`${base(celebrationId)}/${requestId}`);
      return unwrap<ServiceRequest>(res.data, 'Failed to load service request');
    } catch (err) {
      throw new Error(handleApiError(err));
    }
  }

  async createRequest(celebrationId: number, input: CreateServiceRequestInput): Promise<ServiceRequest> {
    try {
      const res = await apiClient.post(base(celebrationId), input);
      return unwrap<ServiceRequest>(res.data, 'Failed to create service request');
    } catch (err) {
      throw new Error(handleApiError(err));
    }
  }

  async updateRequest(celebrationId: number, requestId: number, input: UpdateServiceRequestInput): Promise<ServiceRequest> {
    try {
      const res = await apiClient.put(`${base(celebrationId)}/${requestId}`, input);
      return unwrap<ServiceRequest>(res.data, 'Failed to update service request');
    } catch (err) {
      throw new Error(handleApiError(err));
    }
  }

  async deleteRequest(celebrationId: number, requestId: number): Promise<void> {
    try {
      await apiClient.delete(`${base(celebrationId)}/${requestId}`);
    } catch (err) {
      throw new Error(handleApiError(err));
    }
  }

  async transitionStatus(celebrationId: number, requestId: number, status: ServiceRequestStatus): Promise<ServiceRequest> {
    try {
      const res = await apiClient.patch(`${base(celebrationId)}/${requestId}/status`, { status });
      return unwrap<ServiceRequest>(res.data, 'Failed to transition status');
    } catch (err) {
      throw new Error(handleApiError(err));
    }
  }

  // Vendor management

  async listVendors(celebrationId: number, requestId: number): Promise<ServiceRequestVendor[]> {
    try {
      const res = await apiClient.get(vendorBase(celebrationId, requestId));
      return unwrap<ServiceRequestVendor[]>(res.data, 'Failed to load vendors');
    } catch (err) {
      throw new Error(handleApiError(err));
    }
  }

  async addVendor(celebrationId: number, requestId: number, input: AddVendorToRequestInput): Promise<ServiceRequestVendor> {
    try {
      const res = await apiClient.post(vendorBase(celebrationId, requestId), input);
      return unwrap<ServiceRequestVendor>(res.data, 'Failed to add vendor');
    } catch (err) {
      throw new Error(handleApiError(err));
    }
  }

  async removeVendor(celebrationId: number, requestId: number, vendorId: number): Promise<void> {
    try {
      await apiClient.delete(`${vendorBase(celebrationId, requestId)}/${vendorId}`);
    } catch (err) {
      throw new Error(handleApiError(err));
    }
  }

  async submitProposal(celebrationId: number, requestId: number, vendorId: number, input: SubmitProposalInput): Promise<ServiceRequestVendor> {
    try {
      const res = await apiClient.post(`${vendorBase(celebrationId, requestId)}/${vendorId}/proposals`, input);
      return unwrap<ServiceRequestVendor>(res.data, 'Failed to submit proposal');
    } catch (err) {
      throw new Error(handleApiError(err));
    }
  }

  async transitionVendorStatus(celebrationId: number, requestId: number, vendorId: number, status: ServiceRequestVendorStatus): Promise<ServiceRequestVendor> {
    try {
      const res = await apiClient.patch(`${vendorBase(celebrationId, requestId)}/${vendorId}/status`, { status });
      return unwrap<ServiceRequestVendor>(res.data, 'Failed to update vendor status');
    } catch (err) {
      throw new Error(handleApiError(err));
    }
  }

  // Posting lifecycle

  async generatePosting(celebrationId: number, requestId: number): Promise<ServiceRequest> {
    try {
      const res = await apiClient.post(`${base(celebrationId)}/${requestId}/generate-posting`);
      return unwrap<ServiceRequest>(res.data, 'Failed to generate posting');
    } catch (err) {
      throw new Error(handleApiError(err));
    }
  }

  async updatePostingContent(celebrationId: number, requestId: number, content: string): Promise<ServiceRequest> {
    try {
      const res = await apiClient.put(`${base(celebrationId)}/${requestId}/posting-content`, { content });
      return unwrap<ServiceRequest>(res.data, 'Failed to update posting content');
    } catch (err) {
      throw new Error(handleApiError(err));
    }
  }

  async transitionPostingStatus(celebrationId: number, requestId: number, status: PostingStatus): Promise<ServiceRequest> {
    try {
      const res = await apiClient.patch(`${base(celebrationId)}/${requestId}/posting-status`, { status });
      return unwrap<ServiceRequest>(res.data, 'Failed to update posting status');
    } catch (err) {
      throw new Error(handleApiError(err));
    }
  }

  async getFinalTerms(celebrationId: number, requestId: number): Promise<FinalTerms> {
    try {
      const res = await apiClient.get(`/celebrations/${celebrationId}/service-requests/${requestId}/final-terms`);
      return unwrap<FinalTerms>(res.data, 'Failed to load final terms');
    } catch (err) {
      throw new Error(handleApiError(err));
    }
  }

  async listPostAcceptConditions(celebrationId: number, requestId: number): Promise<PostAcceptCondition[]> {
    try {
      const res = await apiClient.get(`/celebrations/${celebrationId}/service-requests/${requestId}/conditions`);
      return unwrap<PostAcceptCondition[]>(res.data, 'Failed to load post-accept conditions');
    } catch (err) {
      throw new Error(handleApiError(err));
    }
  }

  async createPostAcceptCondition(
    celebrationId: number,
    requestId: number,
    payload: { condition_type: PostAcceptCondition['condition_type']; description: string },
  ): Promise<PostAcceptCondition> {
    try {
      const res = await apiClient.post(`/celebrations/${celebrationId}/service-requests/${requestId}/conditions`, payload);
      return unwrap<PostAcceptCondition>(res.data, 'Failed to add post-accept condition');
    } catch (err) {
      throw new Error(handleApiError(err));
    }
  }

  async resolvePostAcceptCondition(
    celebrationId: number,
    requestId: number,
    conditionId: number,
    status: 'satisfied' | 'waived',
  ): Promise<PostAcceptCondition> {
    try {
      const res = await apiClient.patch(
        `/celebrations/${celebrationId}/service-requests/${requestId}/conditions/${conditionId}`,
        { status },
      );
      return unwrap<PostAcceptCondition>(res.data, 'Failed to resolve post-accept condition');
    } catch (err) {
      throw new Error(handleApiError(err));
    }
  }

  async awardVendor(
    celebrationId: number,
    requestId: number,
    payload?: { accepted_quote_id?: number },
  ): Promise<AwardVendorResult> {
    try {
      const res = await apiClient.post(`/celebrations/${celebrationId}/service-requests/${requestId}/award`, payload || {});
      return unwrap<AwardVendorResult>(res.data, 'Failed to award vendor');
    } catch (err) {
      throw new Error(handleApiError(err));
    }
  }

  async signServiceRequest(
    celebrationId: number,
    requestId: number,
    payload?: { signature_note?: string },
  ): Promise<SignServiceRequestResult> {
    try {
      const res = await apiClient.post(`/celebrations/${celebrationId}/service-requests/${requestId}/sign`, payload || {});
      return unwrap<SignServiceRequestResult>(res.data, 'Failed to complete signing');
    } catch (err) {
      throw new Error(handleApiError(err));
    }
  }
}

export const serviceRequestApiService = new ServiceRequestApiService();
